import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Text, Flex, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { blockingIssue_periscope_BlockingIssueTask$key } from '@atlassian/jira-relay/src/__generated__/blockingIssue_periscope_BlockingIssueTask.graphql';
import { TaskItem } from '../../../common/ui/task-item';
import { TaskTypeItem } from '../../../common/ui/task-type';
import { useWorkSuggestionsContainer } from '../../../services/periscope-container';
import { useWorkSuggestionsAction } from '../../../services/use-work-suggestions-action';
import { BlockedIssuesPanel } from './blocked-issue';
import { messages } from './messages';

export const BlockingIssueTask = ({
	fragmentRef,
}: {
	fragmentRef: blockingIssue_periscope_BlockingIssueTask$key;
}) => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment blockingIssue_periscope_BlockingIssueTask on WorkSuggestionsBlockingIssueTask {
				__typename
				id
				url
				title
				issueKey
				issueIconUrl
				blockedIssues {
					... on WorkSuggestionsBlockedIssue {
						...blockedIssue_periscope_BlockedIssuesPanel
					}
				}
			}
		`,
		fragmentRef,
	);
	const renderInfoContent = () => (
		<Flex xcss={blockingIssueTaskStyle} alignItems="center">
			<img width="22px" height="22px" src={data.issueIconUrl} alt="Issue icon" />
			<Text>{data.issueKey}</Text>
			<Text>{data.title}</Text>
		</Flex>
	);
	const { snooze, remove } = useWorkSuggestionsAction();
	const [{ showReactionsButtonForSuggestions }, { hideSuggestionsReactionsButton }] =
		useWorkSuggestionsContainer();

	return (
		<TaskTypeItem
			onRemove={() => remove({ id: data.id, typename: data.__typename })}
			onSnooze={() => snooze({ id: data.id, typename: data.__typename })}
			url={data.url}
			showReactionsButton={
				showReactionsButtonForSuggestions[data.id] === true ||
				showReactionsButtonForSuggestions[data.id] == null
			}
			onReactionsButtonClick={() => hideSuggestionsReactionsButton(data.id)}
			suggestionType={data.__typename}
			id={data.id}
			actionButtonTitle={formatMessage(messages.actionButtonNonFinal)}
		>
			<TaskItem taskInfoContent={renderInfoContent()}>
				<Flex direction="column" xcss={blockedIssuesWrapper}>
					{data.blockedIssues &&
						data.blockedIssues.map((blockedIssueFragmentRef, id) => (
							<BlockedIssuesPanel
								fragmentRef={blockedIssueFragmentRef}
								key={id}
								index={id}
								blockedIssuesLength={data.blockedIssues?.length || 1}
							/>
						))}
				</Flex>
			</TaskItem>
		</TaskTypeItem>
	);
};

const blockingIssueTaskStyle = xcss({
	gap: 'space.100',
});

const blockedIssuesWrapper = xcss({
	borderRadius: 'border.radius.100',
	border: '1px solid',
	borderColor: 'color.border',
	boxSizing: 'border-box',
});
